// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Center, Container } from '@/components/ui/layout';
import { Card } from '@/components/ui/data-display';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { TextInput, PasswordInput, Checkbox } from '@/components/ui/inputs';
import { IconUser, IconMail, IconLock, IconArrowLeft, IconCube } from '@tabler/icons-react';
import { FadeIn, SlideIn, HoverScale } from '@/components/animations';
import { ParallaxBackground, SocialLoginButtons, PasswordStrength } from '../components';

export default function RegisterParallax() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Register attempt:', { name, email, password });
  };

  return (
    <ParallaxBackground preset="circles" mouseParallax intensity={25}>
      <Center mih="100vh" p="md">
        <Container size="xs" w="100%">
          <SlideIn direction="up" delay={100} duration={600}>
            <Card
              p="xl"
              radius="lg"
              style={{
                backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(16px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
              }}
            >
              <Stack gap="lg">
                {/* Back button */}
                <Button
                  component={Link}
                  to="/app/demo/auth-pages/register"
                  variant="subtle"
                  size="xs"
                  leftSection={<IconArrowLeft size={14} />}
                  w="fit-content"
                >
                  Back to variants
                </Button>

                {/* Header */}
                <FadeIn delay={200}>
                  <Stack align="center" gap="xs">
                    <Badge
                      leftSection={<IconCube size={12} />}
                      variant="gradient"
                      gradient={{ from: 'orange', to: 'red' }}
                      size="lg"
                    >
                      Parallax Effect
                    </Badge>
                    <Title order={2} ta="center" mt="md">
                      Create Account
                    </Title>
                    <Text c="dimmed" size="sm" ta="center">
                      Immersive registration experience
                    </Text>
                  </Stack>
                </FadeIn>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <FadeIn delay={300}>
                      <TextInput
                        label="Full name"
                        placeholder="John Doe"
                        leftSection={<IconUser size={16} />}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        size="md"
                        radius="md"
                      />
                    </FadeIn>

                    <FadeIn delay={350}>
                      <TextInput
                        label="Email"
                        placeholder="your@email.com"
                        leftSection={<IconMail size={16} />}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        size="md"
                        radius="md"
                      />
                    </FadeIn>

                    <FadeIn delay={400}>
                      <div>
                        <PasswordInput
                          label="Password"
                          placeholder="Create a password"
                          leftSection={<IconLock size={16} />}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          size="md"
                          radius="md"
                        />
                        {password && <PasswordStrength password={password} variant="bar" />}
                      </div>
                    </FadeIn>

                    <FadeIn delay={450}>
                      <PasswordInput
                        label="Confirm password"
                        placeholder="Confirm your password"
                        leftSection={<IconLock size={16} />}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        size="md"
                        radius="md"
                        error={confirmPassword && password !== confirmPassword ? "Passwords don't match" : undefined}
                      />
                    </FadeIn>

                    <FadeIn delay={500}>
                      <Checkbox
                        label={
                          <Text size="sm">
                            I agree to the <Anchor size="sm">Terms</Anchor> and{' '}
                            <Anchor size="sm">Privacy Policy</Anchor>
                          </Text>
                        }
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.currentTarget.checked)}
                      />
                    </FadeIn>

                    <FadeIn delay={550}>
                      <HoverScale scale={1.02}>
                        <Button
                          type="submit"
                          fullWidth
                          size="md"
                          radius="md"
                          variant="gradient"
                          gradient={{ from: 'orange', to: 'red' }}
                          disabled={!acceptTerms}
                        >
                          Create account
                        </Button>
                      </HoverScale>
                    </FadeIn>
                  </Stack>
                </form>

                {/* Social Login */}
                <FadeIn delay={600}>
                  <SocialLoginButtons
                    providers={['google', 'github']}
                    layout="horizontal"
                    variant="light"
                  />
                </FadeIn>

                {/* Login Link */}
                <FadeIn delay={650}>
                  <Text ta="center" size="sm">
                    Already have an account?{' '}
                    <Anchor component={Link} to="/app/demo/auth-pages/login/parallax" fw={500}>
                      Sign in
                    </Anchor>
                  </Text>
                </FadeIn>
              </Stack>
            </Card>
          </SlideIn>
        </Container>
      </Center>
    </ParallaxBackground>
  );
}
